import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaCog,
  FaCreditCard,
  FaFileContract,
  FaBell,
  FaShieldAlt,
  FaDatabase,
  FaDownload,
  FaRedo,
  FaTrash,
  FaSave,
  FaCheckCircle,
  FaEnvelope,
  FaPhone,
  FaIdCard,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaExclamationTriangle,
  FaChartLine,
  FaUserShield,
  FaHistory,
  FaCubes,
  FaSlidersH,
} from 'react-icons/fa';

const CorporateSettings = () => {
  const navigate = useNavigate();
  const [activeSettingsTab, setActiveSettingsTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Verification Rules State
  const [verificationRules, setVerificationRules] = useState({
    requirePhoneVerification: true,
    requireCompanyIdVerification: true,
    requireAddressVerification: false,
    requireBankVerification: true,
    autoMarkVerified: true,
    defaultVerificationExpiry: '1 year',
    verificationReminderDays: 30,
    enableBulkVerification: false,
  });

  // Credit Policy State
  const [creditPolicy, setCreditPolicy] = useState({
    defaultCreditLimit: '500000',
    maxCreditLimit: '5000000',
    minCreditLimit: '100000',
    defaultCreditTerms: 'Net 30',
    defaultRiskLevel: 'medium',
    autoCreditFreeze: true,
    creditFreezeThreshold: 30,
    enableAutoApproval: false,
    creditReviewInterval: 'monthly',
    enableCreditLimitAlerts: true,
  });

  // Contract Defaults State
  const [contractDefaults, setContractDefaults] = useState({
    defaultBillingCycle: 'monthly',
    defaultContractTerm: '12',
    defaultPaymentTerms: 'net30',
    autoGenerateContractId: true,
    contractIdPrefix: 'CORP_',
    autoRenewal: true,
    renewalReminderDays: 60,
    enableElectronicSignatures: true,
    contractTemplate: 'standard',
  });

  // Notifications State
  const [notifications, setNotifications] = useState({
    // Email Notifications
    emailOnCreditApproval: true,
    emailOnContractExpiry: true,
    emailOnPaymentDue: false,
    emailOnVerificationComplete: true,

    // SMS Notifications
    smsOnPaymentDue: false,
    smsOnCreditLimitBreach: true,
    smsOnContractRenewal: false,

    // System Alerts
    dailyDigestPendingVerifications: true,
    weeklyPerformanceReport: true,
    monthlyCreditReview: true,
    realTimeRiskAlerts: false,
  });

  // Security & Access State
  const [securitySettings, setSecuritySettings] = useState({
    sessionTimeout: '30',
    twoFactorAuth: false,
    ipWhitelisting: false,
    auditLogRetention: '365',
    maxLoginAttempts: '5',
    passwordExpiry: '90',
    enableApiAccess: false,
    dataEncryption: true,
  });

  // System Configuration
  const [systemConfig, setSystemConfig] = useState({
    autoBackup: true,
    backupFrequency: 'daily',
    enableAuditTrail: true,
    dataRetentionPeriod: '5',
    enablePerformanceMetrics: true,
    enableAutoUpdates: false,
    maintenanceMode: false,
  });

  const [errors, setErrors] = useState({});

  const showSuccessToast = (message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleSaveSettings = async (section) => {
    setSaving(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setSaving(false);
    showSuccessToast(`${section} settings updated successfully`);
  };

  const ToggleSwitch = ({ enabled, onChange, label, description }) => (
    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
      <div className="flex-1">
        <label className="block text-sm font-semibold text-gray-900 mb-1">{label}</label>
        {description && <p className="text-sm text-gray-600">{description}</p>}
      </div>
      <button
        type="button"
        className={`relative inline-flex h-7 w-14 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 ${
          enabled ? 'bg-black' : 'bg-gray-300'
        }`}
        onClick={() => onChange(!enabled)}
      >
        <span
          className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
            enabled ? 'translate-x-7' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );

  const SettingsCard = ({ title, icon, children, onSave }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-black rounded-lg text-white">{icon}</div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-600">Configure {title.toLowerCase()} settings</p>
          </div>
        </div>
        <button
          onClick={onSave}
          disabled={saving}
          className="bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2 font-semibold"
        >
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Saving...
            </>
          ) : (
            <>
              <FaSave className="text-sm" />
              Save Changes
            </>
          )}
        </button>
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );

  const settingsTabs = [
    { id: 'general', label: 'General Settings', icon: <FaCog /> },
    { id: 'credit', label: 'Credit Policy', icon: <FaCreditCard /> },
    { id: 'contract', label: 'Contract Rules', icon: <FaFileContract /> },
    { id: 'notifications', label: 'Notifications', icon: <FaBell /> },
    { id: 'security', label: 'Security', icon: <FaShieldAlt /> },
    { id: 'system', label: 'System', icon: <FaCubes /> },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Toast Notification */}
      {showToast && (
        <div className="fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-xl z-50 transform transition-all duration-300 ease-in-out flex items-center gap-3">
          <FaCheckCircle className="text-lg" />
          <span className="font-semibold">{toastMessage}</span>
        </div>
      )}

      {/* Header Section */}
      <header className="flex justify-between items-center mb-3 bg-white p-3">
        <h1 className="text-black text-xl font-bold ml-2">Customer Management</h1>
        <div className="flex space-x-6">
          <button
            onClick={() => navigate('/dashboard/mdm/customer-management/corporate-overview')}
            className="btn"
          >
            Overview
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/customer-management/leisure')}
            className="btn"
          >
            Guest Management
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/customer-management/corporate')}
            className="btn"
          >
            Corporate Accounts
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/customer-management/contracts')}
            className="btn"
          >
            Contracts
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/customer-management/credit-verification')}
            className="btn"
          >
            Credit & Verification
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/customer-management/settings')}
            className="btn text-black font-bold"
          >
            Settings
          </button>
        </div>
      </header>

      <div className="px-4">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">System Settings</h1>
          <p className="text-gray-600 text-sm">
            Manage and configure all corporate customer management settings
          </p>
        </div>

        {/* Settings Navigation Tabs */}
        <div className="bg-white rounded-xl border border-gray-200 p-2 mb-8">
          <div className="flex space-x-1">
            {settingsTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveSettingsTab(tab.id)}
                className={`flex items-center gap-3 px-4 py-4 rounded-lg text-sm font-semibold transition-all duration-200 flex-1 justify-center ${
                  activeSettingsTab === tab.id
                    ? 'bg-black text-white shadow-lg'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="space-y-6">
          {/* General Settings */}
          {activeSettingsTab === 'general' && (
            <SettingsCard
              title="Verification & Onboarding"
              icon={<FaIdCard />}
              onSave={() => handleSaveSettings('Verification')}
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900 border-b pb-2">
                    Verification Requirements
                  </h4>
                  <ToggleSwitch
                    enabled={verificationRules.requirePhoneVerification}
                    onChange={(checked) =>
                      setVerificationRules((prev) => ({
                        ...prev,
                        requirePhoneVerification: checked,
                      }))
                    }
                    label="Phone Verification"
                    description="Require phone number verification for all corporate accounts"
                  />
                  <ToggleSwitch
                    enabled={verificationRules.requireCompanyIdVerification}
                    onChange={(checked) =>
                      setVerificationRules((prev) => ({
                        ...prev,
                        requireCompanyIdVerification: checked,
                      }))
                    }
                    label="Company ID Verification"
                    description="Verify company registration and business documents"
                  />
                  <ToggleSwitch
                    enabled={verificationRules.requireBankVerification}
                    onChange={(checked) =>
                      setVerificationRules((prev) => ({
                        ...prev,
                        requireBankVerification: checked,
                      }))
                    }
                    label="Bank Account Verification"
                    description="Require bank statement verification"
                  />
                  <ToggleSwitch
                    enabled={verificationRules.requireAddressVerification}
                    onChange={(checked) =>
                      setVerificationRules((prev) => ({
                        ...prev,
                        requireAddressVerification: checked,
                      }))
                    }
                    label="Address Verification"
                    description="Verify physical business address"
                  />
                </div>

                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900 border-b pb-2">
                    Verification Settings
                  </h4>
                  <ToggleSwitch
                    enabled={verificationRules.autoMarkVerified}
                    onChange={(checked) =>
                      setVerificationRules((prev) => ({ ...prev, autoMarkVerified: checked }))
                    }
                    label="Auto Complete Verification"
                    description="Automatically mark as verified when all checks pass"
                  />
                  <ToggleSwitch
                    enabled={verificationRules.enableBulkVerification}
                    onChange={(checked) =>
                      setVerificationRules((prev) => ({ ...prev, enableBulkVerification: checked }))
                    }
                    label="Bulk Verification"
                    description="Enable bulk verification processing"
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        Verification Expiry
                      </label>
                      <select
                        value={verificationRules.defaultVerificationExpiry}
                        onChange={(e) =>
                          setVerificationRules((prev) => ({
                            ...prev,
                            defaultVerificationExpiry: e.target.value,
                          }))
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
                      >
                        <option value="6 months">6 Months</option>
                        <option value="1 year">1 Year</option>
                        <option value="2 years">2 Years</option>
                        <option value="3 years">3 Years</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        Reminder Days
                      </label>
                      <input
                        type="number"
                        value={verificationRules.verificationReminderDays}
                        onChange={(e) =>
                          setVerificationRules((prev) => ({
                            ...prev,
                            verificationReminderDays: e.target.value,
                          }))
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
                        min="1"
                        max="90"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </SettingsCard>
          )}

          {/* Credit Policy */}
          {activeSettingsTab === 'credit' && (
            <SettingsCard
              title="Credit Policy Management"
              icon={<FaCreditCard />}
              onSave={() => handleSaveSettings('Credit Policy')}
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900 border-b pb-2">
                    Credit Limits
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        Default Credit Limit (₹)
                      </label>
                      <input
                        type="number"
                        value={creditPolicy.defaultCreditLimit}
                        onChange={(e) =>
                          setCreditPolicy((prev) => ({
                            ...prev,
                            defaultCreditLimit: e.target.value,
                          }))
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
                        placeholder="500000"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                          Min Limit (₹)
                        </label>
                        <input
                          type="number"
                          value={creditPolicy.minCreditLimit}
                          onChange={(e) =>
                            setCreditPolicy((prev) => ({ ...prev, minCreditLimit: e.target.value }))
                          }
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
                          placeholder="100000"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                          Max Limit (₹)
                        </label>
                        <input
                          type="number"
                          value={creditPolicy.maxCreditLimit}
                          onChange={(e) =>
                            setCreditPolicy((prev) => ({ ...prev, maxCreditLimit: e.target.value }))
                          }
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
                          placeholder="5000000"
                        />
                      </div>
                    </div>
                  </div>

                  <h4 className="text-lg font-semibold text-gray-900 border-b pb-2 mt-6">
                    Automation
                  </h4>
                  <ToggleSwitch
                    enabled={creditPolicy.autoCreditFreeze}
                    onChange={(enabled) =>
                      setCreditPolicy((prev) => ({ ...prev, autoCreditFreeze: enabled }))
                    }
                    label="Auto Credit Freeze"
                    description="Automatically freeze credit for overdue accounts"
                  />
                  <ToggleSwitch
                    enabled={creditPolicy.enableAutoApproval}
                    onChange={(enabled) =>
                      setCreditPolicy((prev) => ({ ...prev, enableAutoApproval: enabled }))
                    }
                    label="Auto Approval"
                    description="Automatically approve low-risk credit applications"
                  />
                  <ToggleSwitch
                    enabled={creditPolicy.enableCreditLimitAlerts}
                    onChange={(enabled) =>
                      setCreditPolicy((prev) => ({ ...prev, enableCreditLimitAlerts: enabled }))
                    }
                    label="Credit Limit Alerts"
                    description="Send alerts when credit limits are approached"
                  />
                </div>

                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900 border-b pb-2">
                    Policy Settings
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        Default Credit Terms
                      </label>
                      <select
                        value={creditPolicy.defaultCreditTerms}
                        onChange={(e) =>
                          setCreditPolicy((prev) => ({
                            ...prev,
                            defaultCreditTerms: e.target.value,
                          }))
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
                      >
                        <option value="Net 15">Net 15</option>
                        <option value="Net 30">Net 30</option>
                        <option value="Net 45">Net 45</option>
                        <option value="Net 60">Net 60</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        Default Risk Level
                      </label>
                      <select
                        value={creditPolicy.defaultRiskLevel}
                        onChange={(e) =>
                          setCreditPolicy((prev) => ({ ...prev, defaultRiskLevel: e.target.value }))
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        Credit Review Interval
                      </label>
                      <select
                        value={creditPolicy.creditReviewInterval}
                        onChange={(e) =>
                          setCreditPolicy((prev) => ({
                            ...prev,
                            creditReviewInterval: e.target.value,
                          }))
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
                      >
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="yearly">Yearly</option>
                      </select>
                    </div>
                    {creditPolicy.autoCreditFreeze && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                          Freeze Threshold (Days)
                        </label>
                        <input
                          type="number"
                          value={creditPolicy.creditFreezeThreshold}
                          onChange={(e) =>
                            setCreditPolicy((prev) => ({
                              ...prev,
                              creditFreezeThreshold: e.target.value,
                            }))
                          }
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
                          min="1"
                          max="90"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </SettingsCard>
          )}

          {/* Notifications */}
          {activeSettingsTab === 'notifications' && (
            <SettingsCard
              title="Notification Preferences"
              icon={<FaBell />}
              onSave={() => handleSaveSettings('Notification')}
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900 border-b pb-2">
                    Email Notifications
                  </h4>
                  <ToggleSwitch
                    enabled={notifications.emailOnCreditApproval}
                    onChange={(enabled) =>
                      setNotifications((prev) => ({ ...prev, emailOnCreditApproval: enabled }))
                    }
                    label="Credit Approval"
                    description="Notify when credit applications are approved"
                  />
                  <ToggleSwitch
                    enabled={notifications.emailOnContractExpiry}
                    onChange={(enabled) =>
                      setNotifications((prev) => ({ ...prev, emailOnContractExpiry: enabled }))
                    }
                    label="Contract Expiry"
                    description="Send contract expiry reminders"
                  />
                  <ToggleSwitch
                    enabled={notifications.emailOnPaymentDue}
                    onChange={(enabled) =>
                      setNotifications((prev) => ({ ...prev, emailOnPaymentDue: enabled }))
                    }
                    label="Payment Due"
                    description="Payment due date reminders"
                  />
                  <ToggleSwitch
                    enabled={notifications.emailOnVerificationComplete}
                    onChange={(enabled) =>
                      setNotifications((prev) => ({
                        ...prev,
                        emailOnVerificationComplete: enabled,
                      }))
                    }
                    label="Verification Complete"
                    description="Notify when verification process completes"
                  />
                </div>

                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900 border-b pb-2">SMS Alerts</h4>
                  <ToggleSwitch
                    enabled={notifications.smsOnPaymentDue}
                    onChange={(enabled) =>
                      setNotifications((prev) => ({ ...prev, smsOnPaymentDue: enabled }))
                    }
                    label="Payment Due SMS"
                    description="Send SMS for due payments"
                  />
                  <ToggleSwitch
                    enabled={notifications.smsOnCreditLimitBreach}
                    onChange={(enabled) =>
                      setNotifications((prev) => ({ ...prev, smsOnCreditLimitBreach: enabled }))
                    }
                    label="Credit Limit Breach"
                    description="SMS alerts for credit limit breaches"
                  />
                  <ToggleSwitch
                    enabled={notifications.smsOnContractRenewal}
                    onChange={(enabled) =>
                      setNotifications((prev) => ({ ...prev, smsOnContractRenewal: enabled }))
                    }
                    label="Contract Renewal"
                    description="SMS reminders for contract renewals"
                  />
                </div>

                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900 border-b pb-2">
                    System Reports
                  </h4>
                  <ToggleSwitch
                    enabled={notifications.dailyDigestPendingVerifications}
                    onChange={(enabled) =>
                      setNotifications((prev) => ({
                        ...prev,
                        dailyDigestPendingVerifications: enabled,
                      }))
                    }
                    label="Daily Verification Digest"
                    description="Daily summary of pending verifications"
                  />
                  <ToggleSwitch
                    enabled={notifications.weeklyPerformanceReport}
                    onChange={(enabled) =>
                      setNotifications((prev) => ({ ...prev, weeklyPerformanceReport: enabled }))
                    }
                    label="Weekly Performance Report"
                    description="Weekly corporate performance summary"
                  />
                  <ToggleSwitch
                    enabled={notifications.monthlyCreditReview}
                    onChange={(enabled) =>
                      setNotifications((prev) => ({ ...prev, monthlyCreditReview: enabled }))
                    }
                    label="Monthly Credit Review"
                    description="Monthly credit utilization report"
                  />
                  <ToggleSwitch
                    enabled={notifications.realTimeRiskAlerts}
                    onChange={(enabled) =>
                      setNotifications((prev) => ({ ...prev, realTimeRiskAlerts: enabled }))
                    }
                    label="Real-time Risk Alerts"
                    description="Immediate alerts for high-risk activities"
                  />
                </div>
              </div>
            </SettingsCard>
          )}

          {/* Security Settings */}
          {activeSettingsTab === 'security' && (
            <SettingsCard
              title="Security & Access Control"
              icon={<FaShieldAlt />}
              onSave={() => handleSaveSettings('Security')}
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900 border-b pb-2">
                    Access Control
                  </h4>
                  <ToggleSwitch
                    enabled={securitySettings.twoFactorAuth}
                    onChange={(enabled) =>
                      setSecuritySettings((prev) => ({ ...prev, twoFactorAuth: enabled }))
                    }
                    label="Two-Factor Authentication"
                    description="Require 2FA for all admin users"
                  />
                  <ToggleSwitch
                    enabled={securitySettings.ipWhitelisting}
                    onChange={(enabled) =>
                      setSecuritySettings((prev) => ({ ...prev, ipWhitelisting: enabled }))
                    }
                    label="IP Whitelisting"
                    description="Restrict access to specific IP addresses"
                  />
                  <ToggleSwitch
                    enabled={securitySettings.dataEncryption}
                    onChange={(enabled) =>
                      setSecuritySettings((prev) => ({ ...prev, dataEncryption: enabled }))
                    }
                    label="Data Encryption"
                    description="Encrypt all sensitive data at rest"
                  />
                  <ToggleSwitch
                    enabled={securitySettings.enableApiAccess}
                    onChange={(enabled) =>
                      setSecuritySettings((prev) => ({ ...prev, enableApiAccess: enabled }))
                    }
                    label="API Access"
                    description="Enable API access for integrations"
                  />

                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        Session Timeout (min)
                      </label>
                      <input
                        type="number"
                        value={securitySettings.sessionTimeout}
                        onChange={(e) =>
                          setSecuritySettings((prev) => ({
                            ...prev,
                            sessionTimeout: e.target.value,
                          }))
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
                        min="15"
                        max="120"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        Max Login Attempts
                      </label>
                      <input
                        type="number"
                        value={securitySettings.maxLoginAttempts}
                        onChange={(e) =>
                          setSecuritySettings((prev) => ({
                            ...prev,
                            maxLoginAttempts: e.target.value,
                          }))
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
                        min="3"
                        max="10"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900 border-b pb-2">
                    Security Policies
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        Password Expiry (days)
                      </label>
                      <input
                        type="number"
                        value={securitySettings.passwordExpiry}
                        onChange={(e) =>
                          setSecuritySettings((prev) => ({
                            ...prev,
                            passwordExpiry: e.target.value,
                          }))
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
                        min="30"
                        max="365"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        Audit Log Retention (days)
                      </label>
                      <input
                        type="number"
                        value={securitySettings.auditLogRetention}
                        onChange={(e) =>
                          setSecuritySettings((prev) => ({
                            ...prev,
                            auditLogRetention: e.target.value,
                          }))
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
                        min="30"
                        max="1095"
                      />
                    </div>
                  </div>

                  <h4 className="text-lg font-semibold text-gray-900 border-b pb-2 mt-6">
                    Compliance
                  </h4>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h5 className="font-semibold text-blue-900 mb-2">GDPR & Data Protection</h5>
                    <p className="text-sm text-blue-800">
                      All settings comply with data protection regulations. Audit logs are
                      maintained for compliance purposes.
                    </p>
                  </div>
                </div>
              </div>
            </SettingsCard>
          )}

          {/* System Settings */}
          {activeSettingsTab === 'system' && (
            <SettingsCard
              title="System Configuration"
              icon={<FaCubes />}
              onSave={() => handleSaveSettings('System')}
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900 border-b pb-2">
                    Data Management
                  </h4>
                  <ToggleSwitch
                    enabled={systemConfig.autoBackup}
                    onChange={(enabled) =>
                      setSystemConfig((prev) => ({ ...prev, autoBackup: enabled }))
                    }
                    label="Automatic Backups"
                    description="Automatically backup system data"
                  />
                  <ToggleSwitch
                    enabled={systemConfig.enableAuditTrail}
                    onChange={(enabled) =>
                      setSystemConfig((prev) => ({ ...prev, enableAuditTrail: enabled }))
                    }
                    label="Audit Trail"
                    description="Maintain complete audit trail of all activities"
                  />
                  <ToggleSwitch
                    enabled={systemConfig.enablePerformanceMetrics}
                    onChange={(enabled) =>
                      setSystemConfig((prev) => ({ ...prev, enablePerformanceMetrics: enabled }))
                    }
                    label="Performance Metrics"
                    description="Collect and analyze system performance data"
                  />

                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        Backup Frequency
                      </label>
                      <select
                        value={systemConfig.backupFrequency}
                        onChange={(e) =>
                          setSystemConfig((prev) => ({ ...prev, backupFrequency: e.target.value }))
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        Data Retention (years)
                      </label>
                      <input
                        type="number"
                        value={systemConfig.dataRetentionPeriod}
                        onChange={(e) =>
                          setSystemConfig((prev) => ({
                            ...prev,
                            dataRetentionPeriod: e.target.value,
                          }))
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
                        min="1"
                        max="10"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900 border-b pb-2">
                    System Maintenance
                  </h4>
                  <ToggleSwitch
                    enabled={systemConfig.enableAutoUpdates}
                    onChange={(enabled) =>
                      setSystemConfig((prev) => ({ ...prev, enableAutoUpdates: enabled }))
                    }
                    label="Auto Updates"
                    description="Automatically install system updates"
                  />
                  <ToggleSwitch
                    enabled={systemConfig.maintenanceMode}
                    onChange={(enabled) =>
                      setSystemConfig((prev) => ({ ...prev, maintenanceMode: enabled }))
                    }
                    label="Maintenance Mode"
                    description="Put system in maintenance mode"
                  />

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
                    <h5 className="font-semibold text-yellow-900 mb-2">System Information</h5>
                    <div className="text-sm text-yellow-800 space-y-1">
                      <p>• Current Version: 2.1.4</p>
                      <p>• Last Backup: 2024-03-15 02:00</p>
                      <p>• System Uptime: 99.8%</p>
                    </div>
                  </div>

                  <div className="flex gap-4 mt-6">
                    <button className="flex-1 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition duration-200 font-semibold flex items-center gap-2 justify-center">
                      <FaDownload />
                      Export System Logs
                    </button>
                    <button className="flex-1 bg-gray-600 text-white px-4 py-3 rounded-lg hover:bg-gray-700 transition duration-200 font-semibold flex items-center gap-2 justify-center">
                      <FaRedo />
                      Clear Cache
                    </button>
                  </div>
                </div>
              </div>
            </SettingsCard>
          )}
        </div>

        {/* Quick Actions Footer */}
        <div className="mt-8 bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">System Actions</h3>
          <div className="flex flex-wrap gap-4">
            <button className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition duration-200 font-semibold flex items-center gap-3">
              <FaDownload className="text-lg" />
              Export All Settings
            </button>
            <button className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition duration-200 font-semibold flex items-center gap-3">
              <FaRedo className="text-lg" />
              Reset to Defaults
            </button>
            <button className="px-6 py-3 border border-red-300 rounded-lg text-red-700 hover:bg-red-50 transition duration-200 font-semibold flex items-center gap-3">
              <FaTrash className="text-lg" />
              System Diagnostics
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CorporateSettings;
